import React, {useState, useEffect} from "react";
import {useCart} from "../contexts/CartContext";
import {Trash2, Plus, Minus, ShoppingCart} from "lucide-react";
import {FaAmazon} from "react-icons/fa";
import axios from "axios";
import {useNavigate} from "react-router-dom";
import {useSiteOptions} from "../contexts/SiteOptionsContext";
import Hero from "../components/Hero";
import bookshelf from "../assets/bookshelf.jpg";

interface Book {
  id: number;
  title: string;
  author: string;
  slug: string;
  description: string;
  cover_image_url?: string;
  isbn?: string;
  price: number;
  synopsis?: string;
  status?: string;
  preorder_price?: number;
  amazon_url?: string | null;
  in_stock: boolean;
  available_quantity: number;
  low_stock_warning: boolean;
  stock_message: string;
  release_date?: string | null;
}

const Shop: React.FC = () => {
  const {state, dispatch} = useCart();
  const {ecommerceEnabled} = useSiteOptions();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [contentVisible, setContentVisible] = useState(false);
  const navigate = useNavigate();
  const cartRef = React.useRef<HTMLDivElement | null>(null);
  const checkoutRef = React.useRef<HTMLDivElement | null>(null);
  const [isCartInView, setIsCartInView] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const fetchBooks = async () => {
      const started = Date.now();
      try {
        const response = await axios.get("/api/books");
        const allBooks: Book[] = response.data.books || [];
        setBooks(allBooks);
      } catch (err) {
        console.error("Failed to load books:", err);
      } finally {
        const elapsed = Date.now() - started;
        const wait = Math.max(0, 500 - elapsed);
        setTimeout(() => setLoading(false), wait);
      }
    };

    fetchBooks();
  }, []);

  useEffect(() => {
    if (!loading) setContentVisible(true);
    else setContentVisible(false);
  }, [loading]);

  const displayBooks = React.useMemo(() => {
    if (!ecommerceEnabled) {
      return books;
    }

    return books.filter((book) => {
      const status = book.status?.toLowerCase();
      return status === "available" || status === "preorder";
    });
  }, [books, ecommerceEnabled]);

  useEffect(() => {
    if (!ecommerceEnabled && state.items.length > 0) {
      dispatch({type: "CLEAR_CART"});
    }
  }, [dispatch, ecommerceEnabled, state.items.length]);

  const addToCart = (book: Book) => {
    // Check if we can add more of this book
    const currentQuantity =
      state.items.find((item) => item.id === book.id)?.quantity || 0;
    if (currentQuantity >= book.available_quantity) {
      alert(`Cannot add more. Only ${book.available_quantity} available.`);
      return;
    }

    dispatch({
      type: "ADD_ITEM",
      payload: {
        id: book.id,
        title: book.title,
        price: book.price,
        quantity: 1,
        cover_url: book.cover_image_url,
        available_quantity: book.available_quantity,
        status: book.status,
        release_date: book.release_date || null,
      },
    });
  };

  const removeFromCart = (bookId: number) => {
    dispatch({type: "REMOVE_ITEM", payload: bookId});
  };

  const updateQuantity = (bookId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(bookId);
    } else {
      // Check if the new quantity exceeds available stock
      const book = displayBooks.find((b) => b.id === bookId);
      if (book && quantity > book.available_quantity) {
        alert(`Cannot add more than ${book.available_quantity} of this item.`);
        return;
      }

      dispatch({
        type: "UPDATE_QUANTITY",
        payload: {id: bookId, quantity},
      });
    }
  };

  const handleCheckout = () => {
    if (!ecommerceEnabled) {
      alert("Store checkout is currently disabled. Use Amazon links instead.");
      return;
    }
    if (state.items.length === 0) {
      alert("Your cart is empty!");
      return;
    }
    navigate("/checkout");
  };

  const cartItemCount = state.items.reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  const scrollToCart = () => {
    if (cartRef.current) {
      cartRef.current.scrollIntoView({behavior: "smooth", block: "start"});
    }
  };

  // (debug logs removed)

  // Observe cart visibility to toggle floating button
  useEffect(() => {
    if (!ecommerceEnabled) {
      return;
    }
    // Prefer observing the checkout section; fallback to the cart panel
    const target = checkoutRef.current || cartRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsCartInView(entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.25,
        rootMargin: "0px",
      }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [displayBooks.length, ecommerceEnabled, state.items.length]);

  // Track viewport to approximate Tailwind lg breakpoint (>=1024px)
  useEffect(() => {
    const update = () => {
      const mobile =
        typeof window !== "undefined" ? window.innerWidth < 1024 : true;
      setIsMobile(mobile);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <div className="space-y-16">
      {/* Hero */}
      <Hero showShopButton={false} />

      {/* Bookshelf banner with overlay and centered box */}
      <section
        className="py-16 bg-cover bg-center bg-no-repeat relative"
        style={{backgroundImage: `url(${bookshelf})`}}
      >
        <div className="absolute inset-0 bg-primary-100 opacity-80"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-center">
            <div className="bg-primary-300 border-4 border-primary-600 px-8 py-12 shadow-lg text-center">
              <h2 className="text-4xl font-bold text-primary-600">
                Hallie's Bookshop
              </h2>
            </div>
          </div>
        </div>
      </section>

      <div className={ecommerceEnabled ? "grid lg:grid-cols-3 gap-8" : ""}>
        {loading ? (
          <>
            <div
              className={ecommerceEnabled ? "lg:col-span-2" : ""}
              data-testid="shop-skeleton"
            >
              <div className="space-y-6">
                {Array.from({length: 3}).map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg shadow-lg p-4 animate-pulse"
                  >
                    <div className="flex gap-4 sm:gap-6 items-start">
                      <div className="flex-shrink-0 w-24 h-36 sm:w-32 sm:h-48 md:w-40 md:h-60 lg:w-48 lg:h-72 bg-gray-200 border-2 border-primary-600" />
                      <div className="flex-1 min-w-0 space-y-3">
                        <div className="h-6 w-1/2 bg-gray-200 rounded" />
                        <div className="h-4 w-1/3 bg-gray-200 rounded" />
                        <div className="h-16 w-full bg-gray-200 rounded" />
                        <div className="flex items-center justify-between">
                          <div className="h-6 w-24 bg-gray-200 rounded" />
                          <div className="h-10 w-28 bg-gray-200 rounded" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {ecommerceEnabled && (
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-lg p-6 sticky top-8 animate-pulse">
                  <div className="h-6 w-40 bg-gray-200 rounded mb-6" />
                  <div className="space-y-4">
                    {Array.from({length: 2}).map((_, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="w-12 h-16 bg-gray-200 rounded" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-32 bg-gray-200 rounded" />
                          <div className="h-4 w-20 bg-gray-200 rounded" />
                        </div>
                        <div className="h-8 w-8 bg-gray-200 rounded" />
                      </div>
                    ))}
                    <div className="border-t pt-4">
                      <div className="h-6 w-32 bg-gray-200 rounded ml-auto" />
                      <div className="h-10 w-full bg-gray-200 rounded mt-4" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Books Grid */}
            <div
              className={`transition-opacity duration-300 ${
                ecommerceEnabled ? "lg:col-span-2" : ""
              }`}
              style={{opacity: contentVisible ? 1 : 0}}
            >
              {!ecommerceEnabled && (
                <div className="mb-6 rounded-lg border border-primary-200 bg-primary-50 p-4 text-primary-800">
                  The in-site store is currently paused. Use the Amazon links to
                  purchase available titles.
                </div>
              )}
              <div className="space-y-6">
                {displayBooks.map((book) => (
                  <div
                    key={book.id}
                    className="bg-white rounded-lg shadow-lg p-4"
                  >
                    <div className="flex gap-4 sm:gap-6 items-start">
                      {/* Cover image with 2:3 ratio */}
                      <div className="flex-shrink-0 w-24 h-36 sm:w-32 sm:h-48 md:w-40 md:h-60 lg:w-48 lg:h-72 bg-gray-200 border-2 border-primary-600 overflow-hidden">
                        {book.cover_image_url ? (
                          <img
                            src={book.cover_image_url}
                            alt={book.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                            Cover Image
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold text-gray-900 mb-1 line-clamp-2">
                          {book.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          by {book.author}
                        </p>
                        <p className="text-gray-700 mb-4 text-sm md:text-base line-clamp-4">
                          {book.synopsis || book.description}
                        </p>

                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div>
                            <span className="text-lg font-semibold text-gray-900">
                              ${book.price.toFixed(2)}
                            </span>
                            {book.status?.toLowerCase() !== "preorder" && (
                              <div className="text-sm text-gray-500 mt-1">
                                {book.low_stock_warning
                                  ? `Only ${book.available_quantity} left!`
                                  : book.stock_message === "Out of stock"
                                  ? "Out of Stock"
                                  : "In Stock"}
                              </div>
                            )}
                          </div>
                          {ecommerceEnabled ? (
                            <button
                              onClick={() => addToCart(book)}
                              disabled={!book.in_stock}
                              className={`btn ${
                                book.in_stock
                                  ? "btn-primary"
                                  : "btn-secondary opacity-50 cursor-not-allowed"
                              }`}
                            >
                              {book.in_stock ? "Add to Cart" : "Out of Stock"}
                            </button>
                          ) : book.amazon_url ? (
                            <a
                              href={book.amazon_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-primary inline-flex items-center"
                            >
                              <FaAmazon className="mr-2 h-4 w-4" />
                              Buy on Amazon
                            </a>
                          ) : (
                            <span className="text-sm text-gray-500">
                              Amazon link coming soon
                            </span>
                          )}
                        </div>

                        {book.status?.toLowerCase() === "preorder" && (
                          <div className="mt-auto pt-2 text-sm">
                            <span className="inline-block px-2 py-0.5 rounded-full bg-primary-200 text-primary-800 mr-2">
                              Preorder
                            </span>
                            {book.release_date && (
                              <span className="text-gray-600">
                                Releases{" "}
                                {new Date(
                                  book.release_date
                                ).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Cart Sidebar */}
            {ecommerceEnabled && (
              <div className="lg:col-span-1">
                <div
                  ref={cartRef}
                  className="bg-white rounded-lg shadow-lg p-6 sticky top-8"
                >
                  <h2 className="text-xl font-bold text-gray-900 mb-6">
                    Shopping Cart
                  </h2>

                  {state.items.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      Your cart is empty
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {state.items.map((item) => {
                        const book = displayBooks.find((b) => b.id === item.id);
                        const availableQuantity = book?.available_quantity || 0;
                        const isAtMaxQuantity =
                          item.quantity >= availableQuantity;

                        return (
                          <div
                            key={item.id}
                            className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
                          >
                            <div className="w-12 h-16 bg-gray-200 rounded flex items-center justify-center">
                              {item.cover_url ? (
                                <img
                                  src={item.cover_url}
                                  alt={item.title}
                                  className="w-full h-full object-cover rounded"
                                />
                              ) : (
                                <div className="text-center text-gray-500 text-xs">
                                  <p>Cover</p>
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 text-sm">
                                {item.title}
                              </h4>
                              <p className="text-gray-600 text-sm">
                                ${item.price.toFixed(2)}
                              </p>
                              {book?.low_stock_warning && (
                                <p className="text-xs text-yellow-600 font-medium mt-1">
                                  Only {availableQuantity} left!
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  updateQuantity(item.id, item.quantity - 1)
                                }
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <span className="text-sm font-medium w-8 text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() =>
                                  updateQuantity(item.id, item.quantity + 1)
                                }
                                disabled={isAtMaxQuantity}
                                className={`p-1 rounded ${
                                  isAtMaxQuantity
                                    ? "opacity-50 cursor-not-allowed"
                                    : "hover:bg-gray-200"
                                }`}
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="p-1 hover:bg-red-100 rounded text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })}

                      <div ref={checkoutRef} className="border-t pt-4">
                        <div className="flex justify-between text-lg font-semibold">
                          <span>Total:</span>
                          <span>${state.total.toFixed(2)}</span>
                        </div>
                        <button
                          onClick={handleCheckout}
                          className="btn btn-primary w-full mt-4"
                        >
                          Proceed to Checkout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {ecommerceEnabled &&
        !loading &&
        cartItemCount > 0 &&
        !isCartInView &&
        isMobile && (
        <button
          id="floatCartButton"
          type="button"
          onClick={scrollToCart}
          aria-label="View cart"
          className="lg:hidden fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[9999] h-14 w-14 rounded-full bg-primary-700 text-white border-2 border-primary-200 shadow-2xl flex items-center justify-center"
        >
          <ShoppingCart className="h-6 w-6" />
          <span className="absolute -top-2 -right-2 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {cartItemCount}
          </span>
        </button>
      )}
    </div>
  );
};

export default Shop;
